// src/contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, googleProvider } from '../config/firebase';
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import api from '../utils/apiClient';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [sitemapUrlFromSignup, setSitemapUrlFromSignup] = useState('');

  useEffect(() => {
    // Set up listener for auth changes
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);

        // Check if user has completed onboarding from localStorage
        const userOnboardingStatus = localStorage.getItem(`onboarding_${currentUser.uid}`);
        setOnboardingCompleted(userOnboardingStatus === 'completed');

        // Check if there's a sitemap URL stored from signup
        const storedSitemapUrl = localStorage.getItem(`sitemap_url_${currentUser.uid}`);
        if (storedSitemapUrl) {
          setSitemapUrlFromSignup(storedSitemapUrl);
        }

        // Optionally fetch profile from backend to sync onboarding status if not in local storage
        if (userOnboardingStatus !== 'completed') {
          try {
            // We need to wait a bit for the token to be ready or just try
            // But apiClient will get the token automatically
            // Let's just rely on local storage for now to match previous logic, 
            // or we can fetch profile.
            // Previous logic fetched profile in userController but AuthContext only checked localStorage?
            // Wait, previous AuthContext checked localStorage.
            // But userController had getProfile.
            // Let's stick to localStorage for now to minimize changes, 
            // but ideally we should fetch from backend.
          } catch (e) {
            console.error("Error syncing profile", e);
          }
        }

      } else {
        setUser(null);
        setOnboardingCompleted(false);
        setSitemapUrlFromSignup('');
      }

      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Sign up with email and password
  const signUp = async (email, password, sitemapUrl = '') => {
    try {
      setLoading(true);
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // If sitemap URL was provided, store it in localStorage
      if (sitemapUrl && user?.uid) {
        localStorage.setItem(`sitemap_url_${user.uid}`, sitemapUrl);
        setSitemapUrlFromSignup(sitemapUrl);
      }

      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with email and password
  const signIn = async (email, password) => {
    try {
      setLoading(true);
      await signInWithEmailAndPassword(auth, email, password);
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Google
  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithPopup(auth, googleProvider);
      return { success: true };
    } catch (error) {
      setError(error.message);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      setLoading(true);
      await firebaseSignOut(auth);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Complete onboarding
  const completeOnboarding = async () => {
    try {
      if (!user) return { success: false, error: 'No user is logged in' };

      // Store onboarding status in localStorage
      localStorage.setItem(`onboarding_${user.uid}`, 'completed');
      setOnboardingCompleted(true);

      // Also update backend
      try {
        await api.user.updateProfile({ onboarding_completed: true });
      } catch (e) {
        console.error("Failed to update backend profile", e);
      }

      return { success: true };
    } catch (error) {
      console.error('Error completing onboarding:', error);
      return { success: false, error: error.message };
    }
  };

  // Check if user needs to go through onboarding
  const checkOnboardingStatus = () => {
    if (!user) return false;
    return onboardingCompleted;
  };

  const value = {
    user,
    loading,
    error,
    onboardingCompleted,
    sitemapUrlFromSignup,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    completeOnboarding,
    checkOnboardingStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};